import { useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { RunType } from '../../../../../../backend/src/run/run-type';
import { RunStatus } from '../../../../../../backend/src/types/types';
import { useFetchAllActiveRuns } from '@/api/queries/analyticsQueries/useFetchAllActiveRuns';
import { ActiveRun } from '@/api/types/backend-dto-types';
import { formatDuration } from '@/helpers/formatDate';
import { AllRunTypes, RunTypeLabels } from '@/helpers/runs';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { intervalToMinutes } from '@/new/components/processing-status-badge';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { CellWithCopy } from '@/new/components/table/table-cell-with-copy';
import { AllRunStatuses, RunStatusLabels } from '@/new/lib/pipeline-status';
import { formatDatetimeForTable, includedInArr } from '@/new/lib/table-utils';
import { RunStatusCell } from '@/new/pages/admin/runs/run-status-cell';

const fallbackTableData: ActiveRun[] = [];
const formatRunStatus = (status: string) => RunStatusLabels[status as RunStatus] ?? status;
const formatMinutes = (minutes: number) => formatDuration(minutes * 60_000);
const formatRunType = (type: string) => RunTypeLabels[type as RunType] ?? type;
const columnHelper = createColumnHelper<ActiveRun>();

const columns = [
  columnHelper.accessor('id', {
    header: () => <span>Run Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('organisation.name', {
    id: 'organisation.name',
    header: () => <span>Organisation</span>,
    cell: (info) => <span className="line-clamp-1 max-w-48">{info.getValue()}</span>,
    filterFn: includedInArr,
  }),
  columnHelper.accessor('matterId', {
    header: () => <span>Matter Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatRunType(info.getValue())}</span>,
    filterFn: includedInArr,
  }),
  columnHelper.accessor('status', {
    header: () => <span>Status</span>,
    cell: (info) => <RunStatusCell runId={info.row.original.id} status={info.getValue()} />,
    filterFn: includedInArr,
  }),
  columnHelper.accessor('docCount', {
    header: () => <span>Docs</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('successfulPagesCount', {
    header: () => <span>Pages Succeeded</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('failedPagesCount', {
    header: () => <span>Pages Failed</span>,
    cell: (info) => {
      const failed = info.getValue();
      return <span className={failed > 0 ? 'text-red-600' : undefined}>{failed}</span>;
    },
  }),
  columnHelper.accessor('createdBy.email', {
    id: 'createdBy.email',
    header: () => <span>Created By</span>,
    cell: (info) => <span>{info.getValue()}</span>,
    filterFn: includedInArr,
  }),
  columnHelper.accessor((row) => intervalToMinutes(row.durationEstimate), {
    id: 'durationEstimate',
    header: () => <span>Est. Duration</span>,
    cell: (info) => {
      const minutes = info.getValue();
      return <span className="whitespace-nowrap">{minutes === null ? '—' : formatMinutes(minutes)}</span>;
    },
  }),
  columnHelper.accessor(
    (row) =>
      row.completedAt
        ? Math.round((new Date(row.completedAt).getTime() - new Date(row.createdDate).getTime()) / 60_000)
        : null,
    {
      id: 'actualDuration',
      header: () => <span>Duration</span>,
      cell: (info) => {
        const minutes = info.getValue();
        return <span className="whitespace-nowrap">{minutes === null ? '—' : formatMinutes(minutes)}</span>;
      },
    },
  ),
  columnHelper.accessor('createdDate', {
    header: () => <span>Date Created</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
  }),
  columnHelper.accessor('completedAt', {
    header: () => <span>Date Completed</span>,
    cell: (info) => {
      const value = info.getValue();
      return <span className="whitespace-nowrap">{value ? formatDatetimeForTable(value) : '—'}</span>;
    },
  }),
];

export function AllRuns() {
  usePlatformPermissionCheck(Permission.PlatformManageRuns);
  const { data: runsData, isLoading } = useFetchAllActiveRuns();
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const data = runsData ?? fallbackTableData;
  const statusOptions = useMemo(() => {
    const present = new Set(data.map((run) => run.status));
    return AllRunStatuses.filter((status) => present.has(status));
  }, [data]);
  const typeOptions = useMemo(() => {
    const present = new Set(data.map((run) => run.type));
    return AllRunTypes.filter((type) => present.has(type));
  }, [data]);
  const organisationOptions = useMemo(
    () => Array.from(new Set(data.map((run) => run.organisation.name))).sort((a, b) => a.localeCompare(b)),
    [data],
  );
  const userOptions = useMemo(
    () =>
      Array.from(new Set(data.map((run) => run.createdBy.email).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  const table = useReactTable({
    data,
    columns,
    // Key rows by run so an open status editor stays on its run when a refetch reorders the list.
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: { sorting: [{ id: 'createdDate', desc: true }], pagination: { pageSize: 15 } },
    state: { globalFilter, columnFilters },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
  });
  const clearAllFilters = () => {
    setGlobalFilter('');
    setColumnFilters([]);
  };
  const organisationFilterValue = (table.getColumn('organisation.name')?.getFilterValue() as string[]) ?? [];
  const statusFilterValue = (table.getColumn('status')?.getFilterValue() as string[]) ?? [];
  const typeFilterValue = (table.getColumn('type')?.getFilterValue() as string[]) ?? [];
  const userFilterValue = (table.getColumn('createdBy.email')?.getFilterValue() as string[]) ?? [];

  return (
    <>
      <Breadcrumb
        items={[
          { href: '/admin/organisations', label: 'Organisations', active: false },
          { href: '/admin/runs', label: 'Recent Runs', active: true },
        ]}
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Recent Runs" />
        </PageHeader>
      </PageContainer>
      <ScrollableTable
        table={table}
        plural="runs"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        loading={isLoading}
        filters={{
          popover: (
            <FilterPopoverContent clearAllFilters={clearAllFilters}>
              <div>
                <p className="mb-3 text-gray-600">Organisation</p>
                <FilterMultiSelect
                  values={organisationFilterValue}
                  options={organisationOptions}
                  column={table.getColumn('organisation.name')!}
                  placeholder="Select organisations..."
                />
              </div>
              <div>
                <p className="mb-3 text-gray-600">Status</p>
                <FilterMultiSelect
                  values={statusFilterValue}
                  options={statusOptions}
                  column={table.getColumn('status')!}
                  placeholder="Select statuses..."
                  formatLabel={formatRunStatus}
                />
              </div>
              <div>
                <p className="mb-3 text-gray-600">Type</p>
                <FilterMultiSelect
                  values={typeFilterValue}
                  options={typeOptions}
                  column={table.getColumn('type')!}
                  placeholder="Select types..."
                  formatLabel={formatRunType}
                />
              </div>
              <div>
                <p className="mb-3 text-gray-600">Created By</p>
                <FilterMultiSelect
                  values={userFilterValue}
                  options={userOptions}
                  column={table.getColumn('createdBy.email')!}
                  placeholder="Select users..."
                />
              </div>
            </FilterPopoverContent>
          ),
          count: columnFilters.length,
          clearAll: clearAllFilters,
        }}
      />
    </>
  );
}
